import collect from "collect.js";

export default class ProductFilterService {
  /**
   * @param {Array<Object>} categories categories arranged in tree structure where children property leads to child categories
   * @param {Integer} categoryId looked up category ID
   * @returns {Object} category object with its subtree of children
   */
  getCategoryFromTree(categories, categoryId) {
    let categoryLookup = categories.find(
      (category) => category.id == categoryId
    );

    if (categoryLookup) return categoryLookup;

    for (const category of categories) {
      categoryLookup = this.getCategoryFromTree(category.children, categoryId);
      if (categoryLookup) return categoryLookup;
    }

    return null;
  }

  /**
   * @param {Object} category category with its children arranged in tree structure where children property leads to child categories
   * @returns {Array<Object>} all child categories recursively in a single list
   */
  getChildCategoriesRecursive(category) {
    let categories = [];

    categories.push(...category.children);

    for (const child of category.children) {
      const currentCategories = this.getChildCategoriesRecursive(child);
      categories.push(...currentCategories);
    }

    return categories;
  }

  /**
   *
   * @param {*} filterData options for filters, see store
   * @param {*} filters currently applied filters, see store
   * @returns sets filters upon startup
   */
  getFiltersFromUrl(filterData, filters) {
    if (!window.location.pathname.includes("/f/")) return filters;

    const queryStringArr = window.location.pathname.split("/f/")[1].split("/");

    for (const queryString of queryStringArr) {
      for (const filterKey in filterData) {
        const filter = filterData[filterKey].find(
          (filter) => filter.slug == queryString
        );
        if (filter) {
          filters[filterKey].push(filter.id);
        }
      }
    }

    return filters;
  }

  getFiltersFromUrlElastic(filterData, filters) {
    if (!window.location.pathname.includes("/f/")) return filters;

    const queryStringArr = window.location.pathname.split("/f/")[1].split("/");

    for (const queryString of queryStringArr) {
      for (const filterKey in filterData) {
        const filter = filterData[filterKey].find(
          (filter) => filter.slug == queryString
        );
        if (filter) {
          filters[filterKey].push(filter.id);
        }
      }
    }

    return filters;
  }

  /**
   *
   * @param {*} filterData options for filters, see store
   * @param {*} filters currently applied filters, see store
   * @returns returns filters array in string/slug format
   */
  getQueryFilters(filters, filterData, order) {
    const queryArr = [];
    for (const key in filters) {
      for (const filterId of filters[key]) {
        queryArr.push(filterData[key].find((f) => f.id == filterId).slug);
      }
    }

    return queryArr.length > 0 ? queryArr.join("/") : "";
  }

  getQueryFiltersElastic(filters, filterData, order) {
    const queryArr = [];
    for (const key in filters) {
      for (const filterId of filters[key]) {
        queryArr.push(filterData[key].find((f) => f.id == filterId).slug);
      }
    }
    return queryArr.length > 0 ? queryArr.join("/") : "";
  }

  /**
   * @param {object} filterData
   * @param {array} coloredProducts
   * @returns Filter data with valid colored products for given option
   */
  extendFilterData(filterData, coloredProducts) {
    for (const key in filterData) {
      const options = filterData[key];
      for (const option of options) {
        option.coloredProducts = coloredProducts.filter((coloredProduct) => {
          return coloredProduct[key].includes(option.id);
        });
      }
    }

    return filterData;
  }

  /**
   * @param {array} coloredProducts
   * @returns Colored products with extended categories - parent is missing by default from DB
   */
  extendColoredProductCategories(categoryTree, coloredProducts) {
    if (!categoryTree) return coloredProducts;

    for (const coloredProduct of coloredProducts) {
      const coloredProductValidCategories = [];

      for (const coloredProductCategoryId of coloredProduct.categories) {
        const validCategories = [];
        this.getValidCategoryIdsRecursive(
          categoryTree,
          coloredProductCategoryId,
          validCategories
        );
        coloredProductValidCategories.push(...validCategories);
      }

      coloredProduct.categories = collect(coloredProductValidCategories)
        .unique()
        .toArray();
    }

    return coloredProducts;
  }

  /**
   *
   * @param {array} categoryTree
   * @param {int} coloredProductCategoryId
   * @param {array} validCategories
   * @param {int} level
   * @returns All valid categories for given colored product
   */
  getValidCategoryIdsRecursive(
    categoryTree,
    coloredProductCategoryId,
    validCategories,
    level = 0
  ) {
    for (const categoryTreeCategory of categoryTree) {
      validCategories[level] = categoryTreeCategory.id;

      if (
        validCategories[validCategories.length - 1] == coloredProductCategoryId
      )
        return;

      if (categoryTreeCategory.children) {
        this.getValidCategoryIdsRecursive(
          categoryTreeCategory.children,
          coloredProductCategoryId,
          validCategories,
          level + 1
        );
      }
      if (
        validCategories[validCategories.length - 1] == coloredProductCategoryId
      )
        return;
    }

    validCategories.splice(validCategories.length - 1);
  }

  /**
   * @param {*} filters currently applied filters, see store
   * @param {*} coloredProducts all colored products of current category or trademark, see store
   * @param {*} categoryTree category tree for current trademark, see store
   * @returns colored products that match the current filter
   */
  getFilteredColoredProducts(filters, coloredProducts, categoryTree) {
    let filteredColoredProducts = coloredProducts;

    for (const filterKey in filters) {
      if (filters[filterKey].length == 0) continue;

      if (filterKey != "categories") {
        filteredColoredProducts = filteredColoredProducts.filter(
          (coloredProduct) => {
            return coloredProduct[filterKey].some((attributeId) =>
              filters[filterKey].includes(attributeId)
            );
          }
        );

        continue;
      }

      //   Different behaviour for categories
      if (!categoryTree) continue;

      const validCategoryIds = this.getValidCategoryIdsForFilter(
        filters,
        categoryTree
      );

      filteredColoredProducts = filteredColoredProducts.filter(
        (coloredProduct) => {
          return coloredProduct.categories.some((category) =>
            validCategoryIds.includes(category.id)
          );
        }
      );
    }

    return filteredColoredProducts;
  }

  /**
   * @param {*} filters currently applied filters, see store
   * @param {*} categoryTree category tree for current trademark, see store
   * @returns all categories that are valid for filter - intersection of parents and their children
   */
  getValidCategoryIdsForFilter(filters, categoryTree) {
    const categoriesByFilter = [];

    for (const categoryId of filters["categories"]) {
      const category = this.getCategoryFromTree(categoryTree, categoryId);
      const childCategories = category
        ? this.getChildCategoriesRecursive(category)
        : [];
      categoriesByFilter.push([category, ...childCategories]);
    }

    let validCategories = categoriesByFilter[0];

    for (let i = 1; i < categoriesByFilter.length; i++) {
      const categoryIds = categoriesByFilter[i].map((category) => category.id);
      validCategories = validCategories.filter((category) =>
        categoryIds.includes(category.id)
      );
    }

    return validCategories.map((category) => category.id);
  }
}
